import React, { useState, useMemo } from 'react';
import { ChevronLeft, MessageCircle, Calendar, Users, Filter, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useFamilyContext } from '../../contexts/FamilyContext';
import AvatarOrInitial from '../Family/AvatarOrInitial';

const responseTypeConfig = {
  project: {
    label: 'Full Project',
    icon: '🚀',
    color: 'teal',
    bgColor: 'bg-teal-50',
    textColor: 'text-teal-600',
    borderColor: 'border-teal-200',
  },
  big3: {
    label: 'Weekly Goal',
    icon: '📋',
    color: 'azure',
    bgColor: 'bg-azure-50',
    textColor: 'text-azure-600',
    borderColor: 'border-azure-200',
  },
  quick: {
    label: 'Quick Start',
    icon: '⚡',
    color: 'orange',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-600',
    borderColor: 'border-orange-200',
  },
  later: {
    label: 'Save for Later',
    icon: '⏰',
    color: 'gray',
    bgColor: 'bg-stone-50',
    textColor: 'text-slate-600',
    borderColor: 'border-stone-200',
  },
  learn: {
    label: 'Research Phase',
    icon: '🔍',
    color: 'violet',
    bgColor: 'bg-violet-50',
    textColor: 'text-violet-600',
    borderColor: 'border-violet-200',
  },
  pass: {
    label: 'Not Interested',
    icon: '✋',
    color: 'red',
    bgColor: 'bg-red-50',
    textColor: 'text-red-600',
    borderColor: 'border-red-200',
  },
};

export default function ParentResponseDashboard() {
  const { recsIdeas, family, currentUser } = useFamilyContext();
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedKid, setSelectedKid] = useState('all');
  const [sortBy, setSortBy] = useState('recent');

  // Transform the data to include response information
  const responseData = useMemo(() => {
    if (!recsIdeas || !family?.kids) return [];

    const responses = [];
    
    recsIdeas.forEach(idea => {
      if (idea.kidResponses) {
        Object.entries(idea.kidResponses).forEach(([kidId, response]) => {
          const kid = family.kids.find(k => k.id === kidId);
          if (kid && response.status !== 'new') {
            responses.push({
              id: `${idea.id}-${kidId}`,
              recommendationId: idea.id,
              recommendationTitle: idea.title,
              recommendationCategory: idea.category,
              kidId,
              kidName: kid.name,
              kidAvatar: kid.avatar,
              responseType: response.status,
              helpRequested: response.helpRequested || [],
              comment: response.comment,
              timing: response.timing,
              timestamp: response.timestamp || idea.created_at,
              isNew: !response.viewed,
            });
          }
        });
      }
    });

    return responses;
  }, [recsIdeas, family]);

  // Filter and sort responses
  const filteredResponses = useMemo(() => {
    let filtered = responseData;

    // Filter by response type
    if (selectedFilter !== 'all') {
      if (selectedFilter === 'needs-help') {
        filtered = filtered.filter(response => response.helpRequested.length > 0);
      } else if (selectedFilter === 'interested') {
        filtered = filtered.filter(response => ['project', 'big3', 'quick'].includes(response.responseType));
      } else {
        filtered = filtered.filter(response => response.responseType === selectedFilter);
      }
    }

    // Filter by kid
    if (selectedKid !== 'all') {
      filtered = filtered.filter(response => response.kidId === selectedKid);
    }

    // Sort responses
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'recent':
          return new Date(b.timestamp) - new Date(a.timestamp);
        case 'kid':
          return a.kidName.localeCompare(b.kidName);
        case 'type':
          return a.responseType.localeCompare(b.responseType);
        default:
          return 0;
      }
    });

    return filtered;
  }, [responseData, selectedFilter, selectedKid, sortBy]);

  // Summary stats
  const stats = useMemo(() => {
    const total = responseData.length;
    const needsHelp = responseData.filter(r => r.helpRequested.length > 0).length;
    const interested = responseData.filter(r => ['project', 'big3', 'quick'].includes(r.responseType)).length;
    const newResponses = responseData.filter(r => r.isNew).length;

    return { total, needsHelp, interested, newResponses };
  }, [responseData]);

  const handleQuickAction = (response, actionType) => {
    // Implement quick actions like scheduling, messaging, etc.
    console.log(`Quick action: ${actionType} for response:`, response);
    
    switch (actionType) {
      case 'schedule':
        // Navigate to calendar or open scheduling modal
        alert(`Schedule kickoff for ${response.kidName}'s ${response.recommendationTitle}`);
        break;
      case 'budget':
        // Open budget discussion
        alert(`Discuss budget for ${response.kidName}'s project`);
        break;
      case 'resources':
        // Provide research resources
        alert(`Provide research resources to ${response.kidName}`);
        break;
      case 'reply':
        // Open messaging interface
        alert(`Reply to ${response.kidName}'s comment`);
        break;
      default:
        break;
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInHours = Math.floor((now - time) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return time.toLocaleDateString();
  };

  return (
    <div className="bg-app-base min-h-screen">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-4">
              <Link 
                to="/recs-ideas" 
                className="p-2 -ml-2 text-stone-600 hover:text-stone-900 hover:bg-stone-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-stone-900">Kid Responses</h1>
                <p className="text-sm text-stone-600">Recent activity on your recommendations</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 border border-stone-200">
            <div className="text-2xl font-bold text-stone-900">{stats.total}</div>
            <div className="text-sm text-stone-600">Total Responses</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-stone-200">
            <div className="text-2xl font-bold text-emerald-600">{stats.interested}</div>
            <div className="text-sm text-stone-600">Interested</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-stone-200">
            <div className="text-2xl font-bold text-orange-600">{stats.needsHelp}</div>
            <div className="text-sm text-stone-600">Need Support</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-stone-200">
            <div className="text-2xl font-bold text-azure-600">{stats.newResponses}</div>
            <div className="text-sm text-stone-600">New Today</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-stone-200 p-6 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-stone-500" />
              <span className="text-sm font-medium text-stone-700">Filter:</span>
            </div>
            
            {/* Response Type Filter */}
            <select
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value)}
              className="px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="all">All Responses</option>
              <option value="interested">Interested</option>
              <option value="needs-help">Needs Support</option>
              <option value="project">Full Projects</option>
              <option value="quick">Quick Starts</option>
              <option value="learn">Research Phase</option>
              <option value="later">Saved for Later</option>
              <option value="pass">Not Interested</option>
            </select>

            {/* Kid Filter */}
            <select
              value={selectedKid}
              onChange={(e) => setSelectedKid(e.target.value)}
              className="px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="all">All Kids</option>
              {family?.kids?.map(kid => (
                <option key={kid.id} value={kid.id}>{kid.name}</option>
              ))}
            </select>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="recent">Most Recent</option>
              <option value="kid">By Kid</option>
              <option value="type">By Response Type</option>
            </select>
          </div>
        </div>

        {/* Response Cards */}
        {filteredResponses.length === 0 ? (
          <div className="bg-white rounded-xl border border-stone-200 p-12 text-center">
            <MessageCircle className="w-12 h-12 text-stone-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-stone-900 mb-2">No responses yet</h3>
            <p className="text-stone-600">
              {selectedFilter === 'all' 
                ? "Your kids haven't responded to any recommendations yet."
                : "No responses match your current filters."
              }
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredResponses.map(response => {
              const config = responseTypeConfig[response.responseType] || responseTypeConfig.project;
              
              return (
                <div 
                  key={response.id}
                  className={`bg-white rounded-xl border p-6 ${response.isNew ? 'border-azure-300 bg-azure-50' : 'border-stone-200'}`}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <AvatarOrInitial
                        avatar={response.kidAvatar}
                        name={response.kidName}
                        size="md"
                      />
                      <div>
                        <div className="font-semibold text-stone-900">{response.kidName}</div>
                        <div className="text-sm text-stone-500">{formatTimeAgo(response.timestamp)}</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {response.isNew && (
                        <span className="bg-azure-100 text-azure-600 px-2 py-1 rounded-full text-xs font-semibold">
                          New
                        </span>
                      )}
                      <div className={`${config.bgColor} ${config.textColor} px-3 py-1 rounded-full text-sm font-semibold`}>
                        <span className="mr-1">{config.icon}</span>
                        {config.label}
                      </div>
                    </div>
                  </div>

                  {/* Recommendation Info */}
                  <div className="mb-4">
                    <h4 className="font-semibold text-stone-900 mb-1">
                      {response.recommendationTitle}
                    </h4>
                    <span className="text-sm text-stone-500 capitalize">
                      {response.recommendationCategory}
                    </span>
                  </div>

                  {/* Help Requested */}
                  {response.helpRequested.length > 0 && (
                    <div className="mb-4 p-3 bg-orange-50 rounded-lg border border-orange-200">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="w-4 h-4 text-orange-600" />
                        <span className="text-sm font-semibold text-orange-700">Support Requested:</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {response.helpRequested.map((help, index) => (
                          <span
                            key={index}
                            className="bg-white border border-orange-200 text-orange-700 px-2 py-1 rounded text-xs font-medium"
                          >
                            {help}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Comment */}
                  {response.comment && (
                    <div className="mb-4 p-3 bg-stone-50 rounded-lg border-l-3 border-teal-500">
                      <div className="text-sm text-stone-700 italic">
                        "{response.comment}"
                      </div>
                    </div>
                  )}

                  {/* Quick Actions */}
                  <div className="flex flex-wrap gap-2">
                    {response.helpRequested.includes('Materials & Budget') && (
                      <button
                        onClick={() => handleQuickAction(response, 'budget')}
                        className="flex items-center gap-2 px-3 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Budget Discussion
                      </button>
                    )}
                    
                    {['project', 'big3', 'quick'].includes(response.responseType) && (
                      <button
                        onClick={() => handleQuickAction(response, 'schedule')}
                        className="flex items-center gap-2 px-3 py-2 bg-azure-600 text-white text-sm font-medium rounded-lg hover:bg-azure-700 transition-colors"
                      >
                        <Calendar className="w-4 h-4" />
                        Schedule Kickoff
                      </button>
                    )}
                    
                    {response.helpRequested.includes('Research Resources') && (
                      <button
                        onClick={() => handleQuickAction(response, 'resources')}
                        className="flex items-center gap-2 px-3 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 transition-colors"
                      >
                        <Users className="w-4 h-4" />
                        Research Resources
                      </button>
                    )}
                    
                    {response.comment && (
                      <button
                        onClick={() => handleQuickAction(response, 'reply')}
                        className="flex items-center gap-2 px-3 py-2 bg-stone-600 text-white text-sm font-medium rounded-lg hover:bg-stone-700 transition-colors"
                      >
                        <MessageCircle className="w-4 h-4" />
                        Reply
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}